import { ConfigurationError } from "./errors-CEDWDXew.js";
import { getAnthropicModel, setAnthropicBaseUrl } from "./anthropic-provider-CRd6GA4E.js";
import { getAzureOpenAIApiSurface, getAzureOpenAIModel, setAzureOpenAIBaseUrl } from "./azure-openai-provider-BNAjkBmg.js";
import { getOpenAIModel, setOpenAIBaseUrl } from "./openai-provider-DK3FdwQ_.js";
import { getOpenRouterModel, getOpenRouterProviderOptions } from "./openrouter-provider-JT8w8qQ6.js";

//#region src/internal/agents/provider-factory.ts
let configuredProvider;
/**
* Configure the embedded agent provider explicitly.
* Call this from CLI to override auto-detection.
*/
function setAgentProvider(provider) {
	configuredProvider = provider;
}
/**
* Configure base URLs for providers (called from CLI).
*/
function setProviderBaseUrls(config) {
	setOpenAIBaseUrl(config.openaiBaseUrl);
	setAzureOpenAIBaseUrl(config.openaiBaseUrl);
	setAnthropicBaseUrl(config.anthropicBaseUrl);
}
/**
* Check if both API keys are present, indicating a conflict.
*/
function detectProviderConflict() {
	return [
		process.env.ANTHROPIC_API_KEY,
		process.env.OPENAI_API_KEY,
		process.env.OPENROUTER_API_KEY
	].filter(Boolean).length > 1;
}
/**
* Check if the API key exists for a given provider type.
*/
function hasApiKeyForProvider(type) {
	switch (type) {
		case "anthropic": return Boolean(process.env.ANTHROPIC_API_KEY);
		case "openai":
		case "azure-openai": return Boolean(process.env.OPENAI_API_KEY);
		case "openrouter": return Boolean(process.env.OPENROUTER_API_KEY);
	}
}
/**
* Get the expected API key name for a provider type.
*/
function getApiKeyName(type) {
	switch (type) {
		case "anthropic": return "ANTHROPIC_API_KEY";
		case "openai":
		case "azure-openai": return "OPENAI_API_KEY";
		case "openrouter": return "OPENROUTER_API_KEY";
	}
}
/**
* Build a provider instance for the given type.
*/
function buildProvider(type) {
	switch (type) {
		case "anthropic": return {
			type: "anthropic",
			label: "anthropic",
			getModel: getAnthropicModel,
			getProviderOptions: () => ({})
		};
		case "openai": return {
			type: "openai",
			label: "openai responses API",
			getModel: getOpenAIModel,
			getProviderOptions: () => ({ openai: {
				structuredOutputs: false,
				strictJsonSchema: false
			} })
		};
		case "azure-openai": {
			const apiSurface = getAzureOpenAIApiSurface();
			return {
				type: "azure-openai",
				label: `azure-openai ${apiSurface === "chat-completions" ? "chat completions" : "responses"} API`,
				getModel: getAzureOpenAIModel,
				getProviderOptions: () => ({ openai: {
					structuredOutputs: false,
					strictJsonSchema: false
				} })
			};
		}
		case "openrouter": return {
			type: "openrouter",
			label: "openrouter",
			getModel: getOpenRouterModel,
			getProviderOptions: getOpenRouterProviderOptions
		};
	}
}
/**
* Resolve provider type from configuration and environment.
* Returns undefined if no provider can be resolved or if there's a conflict.
*/
function resolveProviderType() {
	if (configuredProvider) return configuredProvider;
	const envProvider = process.env.EMBEDDED_AGENT_PROVIDER?.toLowerCase();
	if (envProvider === "openai" || envProvider === "azure-openai" || envProvider === "anthropic" || envProvider === "openrouter") return envProvider;
	if (detectProviderConflict()) return void 0;
	if (process.env.ANTHROPIC_API_KEY) return "anthropic";
	if (process.env.OPENAI_API_KEY) return "openai";
	if (process.env.OPENROUTER_API_KEY) return "openrouter";
	return void 0;
}
/**
* Get the current agent provider based on configuration.
*
* Resolution order:
* 1. Explicit configuration via setAgentProvider()
* 2. EMBEDDED_AGENT_PROVIDER environment variable
* 3. Detect conflicts when both API keys are present
* 4. Auto-detect: check ANTHROPIC_API_KEY, then OPENAI_API_KEY
* 5. Throw ConfigurationError if no provider available
*/
function getAgentProvider() {
	const providerType = resolveProviderType();
	if (providerType && !hasApiKeyForProvider(providerType)) throw new ConfigurationError(`Provider "${providerType}" is configured but ${getApiKeyName(providerType)} is not set. Please set the API key environment variable.`);
	if (!providerType && detectProviderConflict()) throw new ConfigurationError("Multiple LLM API keys are set. Please specify which provider to use by setting the EMBEDDED_AGENT_PROVIDER environment variable to 'openai', 'azure-openai', 'anthropic', or 'openrouter'.");
	if (!providerType) throw new ConfigurationError("No embedded agent provider configured. Set OPENAI_API_KEY, ANTHROPIC_API_KEY, or OPENROUTER_API_KEY environment variable, or use --agent-provider flag to specify a provider.");
	return buildProvider(providerType);
}
/**
* Check if an embedded agent provider is available.
* Returns true if getAgentProvider() would succeed.
*/
function hasAgentProvider() {
	try {
		getAgentProvider();
		return true;
	} catch {
		return false;
	}
}
/**
* Get the resolved provider type without throwing.
* Returns undefined if no provider is available, API key is missing, or both API keys are present without explicit selection.
*/
function getResolvedProviderType() {
	try {
		return getAgentProvider().type;
	} catch {
		return void 0;
	}
}

//#endregion
export { getAgentProvider, getResolvedProviderType, hasAgentProvider, setAgentProvider, setProviderBaseUrls };
//# sourceMappingURL=provider-factory-Cs_2DBhe.js.map